import { Box, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { MonthlyTimesheet, TimesheetStatus } from '../../../types';
import { useTimesheets } from '../../../queries/hooks/useTimesheets';
import { isSmallScreenState } from '../../../state/UIState';
import { TableSkeletonLoader } from '../../../components/SkeletonLoader/Table.SkeletonLoader';
import { Typography } from '../../../components/Typography/Typography';
import { UserAvatar } from '../../../components/UserAvatar/UserAvatar';
import { TimesheetRouteIdProvider } from '../../SingleTimesheet/context/TimesheetRouteIdContext';
import { SingleTimesheetTable } from '../../SingleTimesheet/components/SingleTimesheetTable/SingleTimesheetTable';
import {
  PendingMonthViewProvider,
  timesheetWeekStartsInCalendarMonth,
} from '../../IssuedTimesheets/PendingMonthViewContext';
import {
  compareTimesheetsByWeekStart,
  pendingMonthTimesheetDomId,
} from '../../../utils/pendingTimesheetsMonthWeeks';
import { TimesheetNavigation } from './TimesheetNavigation';
import { TimesheetWorkflowStrip } from '../../../components/WorkflowStages/TimesheetWorkflowStrip';

type Props = {
  timesheet: MonthlyTimesheet;
};

/** Compact "TS Issued" pills, mirroring the OP pending month view. */
function MonthSummaryPill({ label }: { label: string }) {
  const { colors } = useTheme();
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        px: '6px',
        borderRadius: 999,
        bgcolor: alpha(colors.text.main, 0.06),
        color: colors.text.secondary,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: '0.02em',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

/**
 * Accountant-facing, fillable weekly view for an Issued monthly timesheet. Mirrors the OP
 * `/pending-timesheets/month/:year/:month` layout, but is scoped to the owning OP (no "(You)"),
 * lives under the accountant single-timesheet breadcrumb, and reuses the accountant month `<>` nav.
 */
export function AccountantMonthFillView({ timesheet }: Props) {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const opId = timesheet.user?.id;
  const year = timesheet.year;
  const month = timesheet.month;

  const { isLoading, data: timesheets } = useTimesheets({ userId: opId });

  const { issuedCount, rejectedCount, submittedCount } = useMemo(() => {
    let issued = 0;
    let rejected = 0;
    let submitted = 0;
    for (const ts of timesheets ?? []) {
      if (!timesheetWeekStartsInCalendarMonth(ts, year, month)) continue;
      if (ts.status === TimesheetStatus.REJECTED) rejected += 1;
      else if (ts.status === TimesheetStatus.ISSUED) issued += 1;
      else if (
        ts.status === TimesheetStatus.PENDING_ACCOUNTANT ||
        ts.status === TimesheetStatus.PENDING_OP_MANAGER ||
        ts.status === TimesheetStatus.PENDING_DEAL_OWNERS ||
        ts.status === TimesheetStatus.CLOSED
      ) {
        submitted += 1;
      }
    }
    return { issuedCount: issued, rejectedCount: rejected, submittedCount: submitted };
  }, [timesheets, year, month]);

  // Accountant acts on behalf of the owner, so both Issued and Rejected weeks are editable.
  const monthViewWeeksOrdered = useMemo(() => {
    if (!timesheets) return [];
    return timesheets
      .filter((ts) => timesheetWeekStartsInCalendarMonth(ts, year, month))
      .filter((ts) => {
        const submitted =
          ts.status === TimesheetStatus.PENDING_ACCOUNTANT ||
          ts.status === TimesheetStatus.PENDING_OP_MANAGER ||
          ts.status === TimesheetStatus.PENDING_DEAL_OWNERS ||
          ts.status === TimesheetStatus.CLOSED;
        const editable =
          ts.status === TimesheetStatus.ISSUED || ts.status === TimesheetStatus.REJECTED;
        return editable || submitted;
      })
      .sort(compareTimesheetsByWeekStart)
      .reverse();
  }, [timesheets, year, month]);

  const summaryPills = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
      {issuedCount > 0 && <MonthSummaryPill label={`${issuedCount} TS Issued`} />}
      {rejectedCount > 0 && <MonthSummaryPill label={`${rejectedCount} TS Sent Back`} />}
      {submittedCount > 0 && <MonthSummaryPill label={`${submittedCount} TS Submitted`} />}
    </Box>
  );

  const header = (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: isSmallScreen ? 'flex-start' : 'center',
        width: '100%',
        rowGap: 1,
        mb: 3,
      }}
    >
      <Box
        sx={{
          width: isSmallScreen ? '100%' : '35%',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {timesheet.user?.name ? (
          <>
            <UserAvatar
              name={timesheet.user.name}
              size='medium'
              style={{ width: 34, height: 34, minWidth: 34 }}
              lettersStyle={{ color: colors.icon.inverse, fontSize: 13, lineHeight: '34px' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
              <Typography variant='body' color={colors.text.secondary} style={{ fontSize: '14px' }}>
                {timesheet.user.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                <Chip
                  style={{
                    backgroundColor: colors.surfaceStatus.noAction,
                    minWidth: '66px',
                    height: '28px',
                  }}
                  label={
                    <Typography variant='body' color={colors.text.main}>
                      Submitter
                    </Typography>
                  }
                />
                {summaryPills}
              </Box>
            </Box>
          </>
        ) : null}
      </Box>
      <TimesheetNavigation timesheet={timesheet} />
      {!isSmallScreen && <Box sx={{ width: '35%' }} aria-hidden />}
    </Box>
  );

  if (isLoading || !timesheets) return <TableSkeletonLoader />;

  return (
    <>
      {header}
      {timesheet.user?.workflow && (
        <Box sx={{ mb: 3 }}>
          <TimesheetWorkflowStrip workflow={timesheet.user.workflow} align='flex-start' showName />
        </Box>
      )}
      <PendingMonthViewProvider viewedYear={year} viewedMonth={month} allTimesheets={timesheets}>
        {monthViewWeeksOrdered.length === 0 ? (
          <Typography variant='body' color={colors.text.secondary} style={{ paddingTop: 16 }}>
            No timesheets for this month.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {monthViewWeeksOrdered.map((ts) => {
              const readOnlySubmitted =
                ts.status === TimesheetStatus.PENDING_ACCOUNTANT ||
                ts.status === TimesheetStatus.PENDING_OP_MANAGER ||
                ts.status === TimesheetStatus.PENDING_DEAL_OWNERS ||
                ts.status === TimesheetStatus.CLOSED;
              return (
                <Box key={ts.id} id={pendingMonthTimesheetDomId(ts.id)} sx={{ borderRadius: 1 }}>
                  <TimesheetRouteIdProvider timesheetId={String(ts.id)}>
                    <SingleTimesheetTable
                      embeddedInMonthView
                      readOnlySubmitted={readOnlySubmitted}
                      monthListTimesheet={readOnlySubmitted ? ts : undefined}
                    />
                  </TimesheetRouteIdProvider>
                </Box>
              );
            })}
          </Box>
        )}
      </PendingMonthViewProvider>
    </>
  );
}
